// SEO landing page for an individual agent category.
//
// Today the in-app catalog (/agents) drives category clicks through a
// query-string filter (`/agents?category=<slug>`). That keeps the
// authenticated experience working but gives crawlers a single URL with
// nothing category-specific in the prerendered HTML. This route fixes that:
//
//   /agents/category/<slug>
//
// receives server metadata, then reuses the existing `AgentsPage` and its
// loaded category store for client metadata. No second category fetch is needed.
// The query-string form keeps working unchanged.
//
// Mirrors the SEO pattern used by /tools/[slug], /blog/[slug],
// /tasks/template/[slug], and /explore/category/[slug].
import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { useAgentStore } from '@/store';
import { AgentsPage } from '@/app/(app)/agents/index';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

// ---------------------------------------------------------------------------
// MetaHead helpers
// ---------------------------------------------------------------------------
const SITE_URL = 'https://gab.ai';

const buildCategoryTitle = (cat) => {
  if (cat?.meta?.title) return cat.meta.title;
  if (cat?.name) return `${cat.name} AI Bots — Gab AI`;
  return 'AI Bots Category';
};

const buildCategoryDescription = (cat) => {
  if (cat?.meta?.description) return cat.meta.description;
  if (cat?.description) return cat.description;
  if (cat?.name) {
    return `Browse specialized AI bots for ${cat.name.toLowerCase()} on Gab AI. Pick a bot and start a conversation.`;
  }
  return 'Browse Gab AI’s catalog of specialized AI bots. Pick a bot and start a conversation.';
};

const buildKeywords = (cat) => {
  if (cat?.meta?.keywords) return cat.meta.keywords;
  const base = ['AI bots', 'AI assistants', 'specialized AI', 'Gab AI bots'];
  if (cat?.name) base.unshift(cat.name, `${cat.name} AI`, `AI ${cat.name}`);
  return base.join(', ');
};

const buildStructuredData = (cat, agents, slug) => {
  const url = `${SITE_URL}/agents/category/${slug}`;
  const itemList = (agents || []).slice(0, 30).map((agent, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: agent.slug ? `${SITE_URL}/agents/${encodeURIComponent(agent.slug)}` : `${SITE_URL}/g/${agent.id}`,
    name: agent.name,
    ...(agent.avatarPath ? { image: agent.avatarPath } : {}),
  }));

  const collection = {
    '@type': 'CollectionPage',
    name: buildCategoryTitle(cat),
    description: buildCategoryDescription(cat),
    url,
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    inLanguage: 'en-US',
    ...(itemList.length
      ? {
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: itemList.length,
            itemListElement: itemList,
          },
        }
      : {}),
  };

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'AI Bots', item: `${SITE_URL}/agents` },
      {
        '@type': 'ListItem',
        position: 3,
        name: cat?.name || slug,
        item: url,
      },
    ],
  };

  return { '@context': 'https://schema.org', '@graph': [collection, breadcrumbs] };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AgentCategoryRoute() {
  const params = useLocalSearchParams();
  const slug = typeof params?.slug === 'string' ? params.slug
    : Array.isArray(params?.slug) ? params.slug[0] || null : null;
  const category = useAgentStore(state => state.categories?.[slug]);
  const currentCategoryId = useAgentStore(state => state.currentCategoryId);
  const categoryAgentIds = useAgentStore(state => state.categoryAgentIds);
  const agentsById = useAgentStore(state => state.agents);
  // The shared list buffer can still belong to the preceding category while
  // navigation loads. Do not publish those bots under the next category URL.
  const ready = !!category?.id && String(currentCategoryId) === String(category.id);
  const seo = useMemo(() => {
    if (!slug || !ready) return null;
    const agents = (categoryAgentIds || []).map(id => agentsById?.[id]).filter(Boolean);
    return {
        title: buildCategoryTitle(category),
        description: buildCategoryDescription(category),
        canonical: `/agents/category/${slug}`,
        keywords: buildKeywords(category),
        structuredData: buildStructuredData(category, agents, slug),
    };
  }, [slug, ready, category, categoryAgentIds, agentsById]);

  return (
    <>
      {seo && <MetaHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={seo.structuredData}
      />}
      <AgentsPage categorySlug={slug || undefined} />
    </>
  );
}
